/*
 * The Sales Channels screen's state, shared by the classic panel and the v3 screen.
 *
 * The report arrives as one row per channel plus a row with no `channelId` — the traffic that
 * arrived without a link. That row is the interesting one, and `attribution` below is what turns it
 * into a sentence: an event whose revenue is mostly unattributed is an event whose marketing spend
 * cannot be judged, and nothing else on this screen says so.
 *
 * `clicks: null` means "never measured", not zero. The DTO is explicit about it and so is every
 * derivation here: a conversion rate over a null is not 0%, it is unknown, and printing 0% would be
 * a measurement nobody took.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type {
  SalesChannelDailyDto, SalesChannelDto, SalesChannelLineRoute, SalesChannelPlatform,
  SalesChannelReportDto,
} from '../../api';

export const PLATFORM_LABEL: Record<SalesChannelPlatform, string> = {
  facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', line: 'LINE', other: 'อื่น ๆ',
};

export interface ChannelAttribution {
  /** Revenue that came through a link, as a share of all tracked revenue. */
  attributedRevenue: number;
  unattributedRevenue: number;
  totalRevenue: number;
  /** 0–100, or null where there is no revenue yet to apportion. */
  attributedShare: number | null;
  /** Per-channel revenue share, biggest first — the meters in the frame's rail. */
  breakdown: { label: string; revenue: number; percent: number }[];
}

export function attributionOf(report: readonly SalesChannelReportDto[]): ChannelAttribution {
  const total = report.reduce((sum, row) => sum + row.revenue, 0);
  const unattributed = report
    .filter((row) => row.channelId === null)
    .reduce((sum, row) => sum + row.revenue, 0);
  const attributed = total - unattributed;
  return {
    attributedRevenue: attributed,
    unattributedRevenue: unattributed,
    totalRevenue: total,
    attributedShare: total > 0 ? (attributed / total) * 100 : null,
    breakdown: report
      .filter((row) => row.revenue > 0)
      .map((row) => ({
        label: row.label,
        revenue: row.revenue,
        percent: total > 0 ? (row.revenue / total) * 100 : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue),
  };
}

export interface SalesChannelsState {
  channels: SalesChannelDto[];
  report: SalesChannelReportDto[];
  daily: SalesChannelDailyDto[];
  attribution: ChannelAttribution;
  days: number;
  setDays: (next: number) => void;
  loading: boolean;
  loadError: string;
  busy: boolean;
  message: string;
  draft: { code: string; label: string; platform: SalesChannelPlatform; lineRoute: SalesChannelLineRoute };
  setDraft: (next: SalesChannelsState['draft']) => void;
  create: () => Promise<void>;
  archive: (id: string) => Promise<void>;
  setLineRoute: (id: string, route: SalesChannelLineRoute) => Promise<void>;
  linkFor: (channel: SalesChannelDto) => string;
}

const EMPTY_DRAFT = {
  code: '', label: '', platform: 'tiktok' as SalesChannelPlatform, lineRoute: 'none' as SalesChannelLineRoute,
};

export function useSalesChannels(token: string, eventSlug?: string): SalesChannelsState {
  const [channels, setChannels] = useState<SalesChannelDto[] | null>(null);
  const [report, setReport] = useState<SalesChannelReportDto[]>([]);
  const [daily, setDaily] = useState<SalesChannelDailyDto[]>([]);
  const [days, setDays] = useState(30);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const load = useCallback(async () => {
    try {
      const [list, rep, series] = await Promise.all([
        adminApi.listSalesChannels(token, eventSlug),
        adminApi.getSalesChannelReport(token, eventSlug),
        adminApi.getSalesChannelDaily(token, days, eventSlug),
      ]);
      setChannels(list);
      setReport(rep);
      setDaily(series);
      setLoadError('');
    } catch {
      setLoadError('โหลดข้อมูลช่องทางการขายไม่สำเร็จ');
    }
  }, [token, eventSlug, days]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setMessage('');
    try {
      await fn();
      await load();
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }, [load]);

  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  return {
    channels: channels ?? [],
    report, daily,
    attribution: useMemo(() => attributionOf(report), [report]),
    days, setDays,
    loading: channels === null && !loadError,
    loadError, busy, message,
    draft, setDraft,
    create: () => run(async () => {
      await adminApi.createSalesChannel(token, {
        code: draft.code,
        platform: draft.platform,
        label: draft.label,
        redirectToLine: draft.lineRoute === 'into_oa',
        resolveFromLine: draft.lineRoute === 'inside_oa',
      }, eventSlug);
      setDraft(EMPTY_DRAFT);
    }, 'สร้างช่องทางไม่สำเร็จ'),
    archive: (id) => run(() => adminApi.archiveSalesChannel(token, id, eventSlug), 'เก็บช่องทางไม่สำเร็จ'),
    setLineRoute: (id, route) =>
      // Reloads rather than patching in place: the link is derived from this flag, so a stale row
      // would show a link that no longer matches where the flag now sends people.
      run(() => adminApi.setSalesChannelLineRoute(token, id, route, eventSlug), 'เปลี่ยนเส้นทาง LINE ไม่สำเร็จ'),
    /*
     * Byte-identical to the classic panel's link, deliberately. This string is pasted into a
     * campaign and cannot be corrected afterwards: `redirect_to_line` sends the visitor through the
     * OA, `from_line` is the marker the landing page reads before deciding which code to remember,
     * and a link that differs between the two shells is attribution quietly lost.
     */
    linkFor: (channel) => {
      const base = `${origin}/e/${eventSlug ?? ''}?ref=${encodeURIComponent(channel.code)}`;
      if (channel.redirect_to_line) return `${base}&redirect_to_line=true`;
      if (channel.resolve_from_line) return `${base}&from_line=true`;
      return base;
    },
  };
}
